import { z } from 'zod';

/**
 * ESQUEMA ZOD de la gira. Es el guardian de las lineas rojas del ADR-003:
 * ninguna config puede colarse con un enlace de compra que no existe, con una
 * fecha fuera de la gira, ni con un venue inventado.
 *
 * Se valida al ARRANCAR la app (`event.config.ts`) y en Vitest
 * (`event.config.test.ts`). Si falla, la app no monta: preferimos un fallo
 * ruidoso a una web mintiendo sobre entradas.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Ventana de la gira: nada fuera de febrero 2027 (ADR-003 §1). */
const TOUR_FIRST_DAY = '2027-02-02';
const TOUR_LAST_DAY = '2027-02-27';

/* `whatsapp` entro el 23-sep-2026 con Funes: un destino REAL que no vende
 * entrada. No es un `coming-soon` —no hay una venta que vaya a abrirse— ni un
 * `on-sale` —no hay ficha de tienda—: se reserva escribiendo al organizador.
 * Se declara como estado propio para que el copy no mienta en ninguno de los
 * dos sentidos, y para que siga siendo IMPOSIBLE pintar un enlace vacio: como
 * `on-sale`, exige su URL. */
export const salesStateSchema = z.enum(['coming-soon', 'on-sale', 'sold-out', 'whatsapp']);

/** Acento cromatico del destino. Se aplica como `data-tone` (styles/tones.css). */
export const heroToneSchema = z.enum(['celeste', 'calido', 'aqua', 'ancestral', 'caribe']);

const isoDateSchema = z
  .string()
  .regex(ISO_DATE, 'La fecha debe ser ISO YYYY-MM-DD')
  .refine((v) => v >= TOUR_FIRST_DAY && v <= TOUR_LAST_DAY, {
    message: `Fecha fuera de la gira 2027 (${TOUR_FIRST_DAY} .. ${TOUR_LAST_DAY})`,
  });

export const programBlockSchema = z
  .object({
    id: z.string().min(1),
    /** Hora local del destino, formato 24h. */
    start: z.string().regex(HHMM, 'Hora invalida (HH:mm, 24h)'),
    /** `null` = bloque abierto ("22:00 -> tertulia"), tal cual el brief. */
    end: z.string().regex(HHMM, 'Hora invalida (HH:mm, 24h)').nullable(),
    title: z.string().min(1),
    description: z.string().min(1).nullable(),
    /** Marca el momento estrella para que la pagina lo destaque. */
    isHighlight: z.boolean(),
    /**
     * Rotulo del bloque TAL COMO lo nombra el mockup, cuando difiere del
     * nombre operativo del brief (los "hitos" de Cancun). `null` = usa `title`.
     */
    highlightTitle: z.string().min(1).nullable(),
    highlightSubtitle: z.string().min(1).nullable(),
  })
  .refine((b) => b.isHighlight || (b.highlightTitle === null && b.highlightSubtitle === null), {
    message: 'Solo un bloque marcado como `isHighlight` puede llevar rotulo de hito',
    path: ['highlightTitle'],
  })
  .refine((b) => b.end === null || b.end > b.start, {
    message: 'El fin del bloque no puede ser anterior a su inicio',
    path: ['end'],
  });

export const programDaySchema = z.object({
  /** 1 y 2 en las conferencias; el retiro de Cancun llega a 3 (28-jul-2026). */
  day: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  date: isoDateSchema,
  /** Titulo largo del dia (brief doc 02 §1). */
  title: z.string().min(1),
  /** Rotulo corto con el que el mockup titula el dia. `null` = usa `title`. */
  shortTitle: z.string().min(1).nullable(),
  blocks: z.array(programBlockSchema).min(1),
});

/**
 * Tipo de entrada (General / VIP de Miami).
 * SIN CAMPO DE PRECIO, a proposito: los precios no existen (ADR-003 §2) y el
 * dia que existan habra que tocar este esquema — es decir, pasar por revision.
 */
export const ticketTierSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  isFeatured: z.boolean(),
});

export const faqItemSchema = z.object({
  id: z.string().min(1),
  question: z.string().min(1),
  answer: z.string().min(1),
  /** true = la respuesta depende de datos que el cliente aun no ha dado. */
  isPending: z.boolean(),
});

/** Ornamentos SVG disponibles. La lista la implementa `components/ornaments`. */
export const ornamentIconSchema = z.enum([
  'star',
  'sun',
  'moon',
  'spiral',
  'lotus',
  'eye',
  'shield',
  'infinity',
  'crown',
  'seed',
  'heart',
  'mountain',
]);

export const benefitSchema = z.object({
  id: z.string().min(1),
  icon: ornamentIconSchema,
  title: z.string().min(1),
  description: z.string().min(1).nullable(),
});

export const eventSchema = z
  .object({
    slug: z.enum(['funes', 'buenos-aires', 'bogota', 'mexico', 'cancun']),
    /** Ruta de la SPA. Se comprueba contra el slug mas abajo. */
    path: z.string().startsWith('/'),
    order: z.number().int().min(1).max(5),

    city: z.string().min(1),
    /**
     * Rotulo CORTO de la ciudad, tal y como la rotula el mockup del hub
     * ("MEXICO", "CANCUN") con la acentuacion restituida (ADR-003 §7).
     * NO es un dato nuevo: es el mismo nombre, en la forma breve que el diseno
     * usa donde no cabe el largo. Sin el, "CIUDAD DE MÉXICO" parte en dos
     * lineas dentro de la tarjeta y pisa la fecha (defecto visto en el hub).
     */
    shortName: z.string().min(1),
    country: z.string().min(1),
    countryCode: z.string().length(2),
    /** Emoji de bandera. Decorativo: SIEMPRE acompanado de texto (a11y). */
    flag: z.string().min(1),

    startDate: isoDateSchema,
    endDate: isoDateSchema,
    timeZone: z.string().min(1),

    /** Titulo OFICIAL completo (ADR-003 §1). Va en `<title>`, meta y tarjetas. */
    title: z.string().min(1),
    /** Parte del titulo que se compone en display. */
    titleMain: z.string().min(1),
    /** Parte del titulo que va en script (Allura). `null` si no la hay. */
    titleAccent: z.string().min(1).nullable(),
    /** Claim manuscrito SUELTO, que no forma parte del titulo (Cancun). */
    scriptClaim: z.string().min(1).nullable(),
    /** Promesa corta bajo el titular, en una sola linea. */
    tagline: z.string().min(1),
    /** La misma promesa partida en las lineas EXACTAS del mockup. */
    promiseLines: z.array(z.string().min(1)).min(1),
    /** Gancho de venta literal del brief (doc 02 §2). No se reescribe. */
    salesHook: z.string().min(1),

    heroTone: heroToneSchema,
    isVip: z.boolean(),
    /** Formato: conferencia de 2 dias o retiro de convivencia (3 dias desde el 28-jul-2026). */
    format: z.enum(['conferencia', 'retiro']),

    /** Vacio = ese destino no usa fila de beneficios (p. ej. Cancun en su mockup). */
    benefits: z.array(benefitSchema),
    /**
     * DOS DIAS LAS CONFERENCIAS, TRES EL RETIRO. Estuvo clavado en `.length(2)`
     * hasta el 28-jul-2026, cuando el cliente mando el «PROGRAMA MODIFICADO» y
     * Cancun paso a 18-19-20 con una jornada de llegada por delante. El tope se
     * mantiene —no es una lista abierta— pero deja de mentir sobre el producto.
     *
     * Y EL MINIMO CAYO EL 23-sep-2026, con Funes: el cliente publico el destino
     * con fecha, sede y contacto, pero SIN horarios. La salida honesta es una
     * lista vacia —«todavia no hay programa»— y no un dia inventado para
     * cumplir el esquema. Lo que sigue prohibido es un dia SIN bloques
     * (`programDaySchema` mantiene su `.min(1)`): si un dia existe, dice algo.
     */
    program: z.array(programDaySchema).max(3),
    faq: z.array(faqItemSchema),

    /**
     * DATOS QUE HOY NO EXISTEN (ADR-003 §2). Se declaran `null` a proposito,
     * NO se omiten: asi el compilador obliga a cada pagina a tratar el vacio.
     */
    venue: z.string().min(1).nullable(),
    ticketUrl: z.url().nullable(),
    salesState: salesStateSchema,
    /** Vacio = ese destino no diferencia tipos de entrada en su mockup. */
    ticketTiers: z.array(ticketTierSchema),
    priceFrom: z.null(),
    capacity: z.null(),
  })
  .refine(
    (e) => (e.titleAccent === null ? e.titleMain === e.title : `${e.titleMain} ${e.titleAccent}` === e.title),
    {
      message: 'titleMain + titleAccent tiene que reconstruir EXACTAMENTE `title`',
      path: ['titleMain'],
    },
  )
  .refine((e) => e.path === `/${e.slug}`, {
    message: 'La ruta debe ser exactamente `/<slug>`',
    path: ['path'],
  })
  .refine((e) => e.endDate >= e.startDate, {
    message: 'endDate no puede ser anterior a startDate',
    path: ['endDate'],
  })
  /* EL ULTIMO DIA, NO EL SEGUNDO. Esta comprobacion decia `program[1]` porque
   * todos los eventos duraban dos dias; con el retiro a tres, `program[1]` es el
   * dia de en medio y la regla habria dado por bueno un programa que acaba antes
   * o despues de `endDate` sin quejarse. Se ancla a los EXTREMOS. */
  .refine((e) => e.program.length === 0 || (e.program[0]?.date === e.startDate && e.program.at(-1)?.date === e.endDate), {
    message: 'El primer y el ultimo dia del programa deben caer en startDate y endDate',
    path: ['program'],
  })
  /* Y los dias intermedios tampoco pueden inventarse: numeracion 1..n correlativa
   * y fechas estrictamente crecientes. Sin esto, un dia repetido o descolocado
   * pasaba el gate mientras la pagina lo pintaba en orden. */
  .refine((e) => e.program.every((d, i) => d.day === i + 1), {
    message: 'Los dias del programa deben ir numerados 1..n sin saltos',
    path: ['program'],
  })
  .refine((e) => e.program.every((d, i) => i === 0 || d.date > (e.program[i - 1]?.date ?? '')), {
    message: 'Las fechas del programa deben ir en orden estrictamente creciente',
    path: ['program'],
  })
  // LINEA ROJA ADR-003 §3: un enlace, o ninguno. Nunca un href roto ni un `#`.
  // `on-sale` (ficha de tienda) y `whatsapp` (reserva escribiendo) EXIGEN su
  // URL; `coming-soon` y `sold-out` exigen que NO la haya.
  .refine((e) => (e.salesState === 'on-sale' || e.salesState === 'whatsapp') === (e.ticketUrl !== null), {
    message:
      'Incoherencia de venta: `on-sale` y `whatsapp` exigen `ticketUrl`, y cualquier otro estado exige `ticketUrl: null`. Prohibido un href roto o un `#`.',
    path: ['ticketUrl'],
  });

export const eventsSchema = z
  .array(eventSchema)
  .length(5, 'La gira tiene exactamente 5 destinos')
  .refine((list) => new Set(list.map((e) => e.slug)).size === list.length, {
    message: 'Hay slugs repetidos',
  })
  .refine((list) => list.every((e, i) => e.order === i + 1), {
    message: 'El campo `order` debe seguir el orden cronologico de la gira, empezando en 1',
  });
